import { useLazyQuery, useQuery } from "@apollo/client";
import { Box, Checkbox, createStyles, FormControlLabel, Grid, makeStyles, Theme } from "@material-ui/core";
import PhoneField from "components/@phone-field";
import Autocomplete from "components/Autocomplete";
import Dialog from "components/Dialog";
import { GeoAutoCompletion } from "components/GeoAutoCompletion";
import { TextField } from "components/TextField";
import { isValidPhoneNumberFormat } from "config/constants";
import { theme } from "config/theme";
import HTTP_STATUS_CODES from "constants/http-status-code";
import useText from "constants/text";
import { ALL_HUBS, GET_HUBS_BY_CITY } from "domains/Task/graphql/queries";
import { Hub, Order } from "generated/graphql";
import { isEmpty } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import "styled-components/macro";
import { ValidReceiver } from "utils";
import { NewJobContext } from "../context";

export interface ReceiverProps {
  open: boolean;
  handleClose: () => void;
  order: Order;
  clientId?: string;
  fixReceiverSimilairyPhoneNumber?: (phoneNumber: string) => void;
  receiverSimilairyPhoneNumber?: boolean;
}

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: "54px 54px",
      [theme.breakpoints.down("xs")]: {
        padding: "13px 10px",
      },
    },
  })
);

export function UpdateOrder({
  open,
  handleClose,
  fixReceiverSimilairyPhoneNumber,
  receiverSimilairyPhoneNumber,
  order,
}: ReceiverProps) {
  const styles = useStyles();
  const [isFormSubmited, setFormSubmited] = React.useState(false);
  const { text } = useText();
  const { setOrders } = React.useContext(NewJobContext);
  const [formValues, setFormValues] = React.useState(order?.order_receiver);
  const [relayValues, setRelayValues] = useState<Hub>(null);
  const [cityValue, setCityValue] = useState<Hub>(null);
  const [relays, setRelays] = useState([]);
  const [getAllHubsByCity, { loading, data: hubsByCity }] = useLazyQuery(GET_HUBS_BY_CITY);
  const { data: relayData } = useQuery(ALL_HUBS);
  const [relayPointCity, setRelayPointCity] = useState([]);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    setChecked(!isEmpty({ ...order?.relay }));
    setCityValue({ ...order?.relay });
  }, [order, open]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  useEffect(() => {
    if (!checked) {
      setRelayValues(null);
      setCityValue(null);
    }
  }, [checked]);

  const handleInputChange = React.useCallback(
    (event: any) => {
      setFormValues({
        ...formValues,
        [event.target.name]: event.target.value,
      });
    },
    [formValues]
  );

  React.useEffect(() => {
    if (cityValue) {
      getAllHubsByCity({
        variables: {
          queries: {
            city: cityValue?.location?.city,
          },
        },
      });
    }
  }, [cityValue]);

  React.useEffect(() => {
    if (order && order?.order_receiver) {
      setFormValues(order?.order_receiver);
      setRelayValues(order?.relay);
    }
  }, [order]);

  React.useEffect(() => {
    if (hubsByCity) {
      const getHubs = hubsByCity?.getAllHubsByCity;
      if (getHubs?.code === HTTP_STATUS_CODES.OK) {
        setRelays([getHubs?.data[0]]);
        setRelayValues(getHubs?.data[0]);
      }
    }
  }, [hubsByCity, open]);

  React.useEffect(() => {
    if (relayData) {
      const hubs = relayData?.allHubs;
      if (hubs?.code === HTTP_STATUS_CODES.OK) {
        setRelayPointCity(hubs?.data);
      }
    }
  }, [relayData]);

  const handleConfirmOrderEdition = () => {
    setOrders((prev) => {
      return prev.map((item) => {
        if (order._id === item._id) {
          const updatedItem = {
            ...item,
            isValid: true,
            order_receiver: {
              ...formValues,
            },
            relay: cityValue,
          };
          return {
            ...updatedItem,
            // pickupAddress: inputValue,
          };
        }
        return item;
      });
    });
    handleClose();
  };

  const onChange = (event: any) => {
    event?.persist();
    setFormValues((prev) => ({
      ...prev,
      [event?.target?.name]: event?.target?.value,
    }));
  };
  const isValidPhone = () => isValidPhoneNumberFormat(formValues.receiver_phone_number);

  const handleSelect = (event: any, value: Hub) => {
    setCityValue(value);
  };

  const handleOnInputRelaypointChange = (event: any, newInputValue: Hub) => {
    setRelayValues(newInputValue);
  };
  const isFormValid = useMemo(() => ValidReceiver.isValidSync(formValues), [formValues]);

  const getCityOptionLabel = (option: Hub) => option?.location?.city || "";
  const getRelayOptionLabel = (relay: Hub) => relay?.name || "";
  return (
    <Dialog
      title={text.all.update_receiver}
      open={open}
      confirmButtonTitle={text.all.update}
      handleClose={() => handleClose()}
      handleConfirm={handleConfirmOrderEdition}
      maxWidth="md"
      key="add receiver dialog"
      disableConfirmButton={!isFormValid || receiverSimilairyPhoneNumber}
      reponsiveHeight="80vh"
    >
      <Grid container spacing={4} className={styles.root}>
        <Grid item md={6} xs={12}>
          <TextField
            fullWidth
            label={text.all.first_name}
            name="receiver_firstname"
            value={formValues?.receiver_firstname}
            onChange={handleInputChange}
            error={isFormSubmited && formValues?.receiver_firstname?.length === 0}
            helperText={
              isFormSubmited && formValues?.receiver_firstname?.length === 0 ? `${text.all.this_field_is_required}` : ""
            }
            required
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <TextField
            fullWidth
            label={text.all.last_name}
            name="receiver_lastname"
            value={formValues?.receiver_lastname}
            onChange={onChange}
            error={isFormSubmited && formValues?.receiver_lastname?.length === 0}
            helperText={
              isFormSubmited && formValues?.receiver_lastname?.length === 0 ? `${text.all.this_field_is_required}` : ""
            }
            required
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <TextField
            fullWidth
            label={text.all.entreprise}
            name="receiver_entreprise"
            value={formValues?.receiver_entreprise}
            onChange={onChange}
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <PhoneField
            fullWidth
            label={text.all.phone_number}
            name="receiver_phone_number"
            value={formValues?.receiver_phone_number}
            onValueChange={(value) => {
              fixReceiverSimilairyPhoneNumber(value);
              setFormValues({
                ...formValues,
                receiver_phone_number: value,
              });
            }}
            required
            error={(formValues.receiver_phone_number !== "" && !isValidPhone()) || receiverSimilairyPhoneNumber}
            helperText={
              isFormSubmited && formValues?.receiver_phone_number.length === 0
                ? `${text.all.this_field_is_required}`
                : isFormSubmited && !isValidPhoneNumberFormat(formValues?.receiver_phone_number)
                ? `${text.all.number_invalid}`
                : receiverSimilairyPhoneNumber
                ? `${text.all.number_already_used}`
                : ""
            }
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <GeoAutoCompletion
            onChange={(values) => setFormValues((prev) => ({ ...prev, receiver_address: values }))}
            address={formValues?.receiver_address}
            error={
              (isFormSubmited && formValues?.receiver_address?.place_id === undefined) ||
              formValues?.receiver_address?.place_id === null ||
              formValues?.receiver_address?.address?.length === 0 ||
              formValues?.receiver_address?.address?.length === undefined
            }
            helperText={
              (isFormSubmited && formValues?.receiver_address?.place_id === undefined) ||
              formValues?.receiver_address?.place_id === null ||
              formValues?.receiver_address?.address?.length === 0 ||
              formValues?.receiver_address?.address?.length === undefined
                ? `${text.all.this_field_is_required}`
                : ""
            }
            required
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <TextField
            fullWidth
            label={text.all.specification}
            name="receiver_specification_address"
            value={formValues?.receiver_specification_address}
            helperText={
              isFormSubmited && formValues?.receiver_specification_address?.length === 0
                ? `${text.all.this_field_is_required}`
                : ""
            }
            error={isFormSubmited && formValues?.receiver_specification_address?.length === 0}
            onChange={onChange}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <Box
            css={`
              ${theme.breakpoints.down("xs")} {
                margin-top: -9px;
              }
            `}
          >
            <FormControlLabel
              css={`
                ${theme.breakpoints.down("xs")} {
                  margin-right: -23px;
                }
              `}
              control={
                <Checkbox
                  checked={checked}
                  color="primary"
                  onChange={handleChange}
                  name="reception_at_relay"
                  inputProps={{ "aria-label": "primary checkbox" }}
                />
              }
              label={text.all.reception_at_relay}
            />
          </Box>
        </Grid>

        <Grid item md={6} xs={12}>
          <Autocomplete
            disabled={!checked}
            options={relayPointCity}
            value={cityValue}
            onChange={handleSelect}
            label={text.all.city}
            name="city"
            getOptionLabel={getCityOptionLabel}
            renderInput={(params: any) => <TextField {...params} variant="outlined" />}
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <Autocomplete
            disabled={!checked || loading || !cityValue}
            options={relays}
            value={cityValue}
            onChange={handleOnInputRelaypointChange}
            label={text.all.relay_point}
            name="name"
            getOptionLabel={getRelayOptionLabel}
            renderInput={(params: any) => <TextField {...params} variant="outlined" />}
          />
        </Grid>
      </Grid>
    </Dialog>
  );
}

export default UpdateOrder;
